import type {InletDescriptor, OutletDescriptor} from "#template/runtime-graph/descriptors"
import type {ImageGenerator, ImageNodeGenerator, InputMaterialConnection, MaterialGraph} from "@cm/material-nodes"
import {BsdfDiffuse, BsdfPrincipled, Color, Hsv, Mapping, materialConnection, MixRgb, UvMap} from "@cm/material-nodes"
import type {Inlet, Outlet} from "#template/runtime-graph/slots"
import {NotReady} from "#template/runtime-graph/slots"
import {TypeDescriptors} from "#template/runtime-graph/type-descriptors"
import type {NodeClassImpl} from "#template/runtime-graph/types"

const TD = TypeDescriptors

type TransformColorOverlayDescriptorType = {
    image: InletDescriptor<ImageGenerator | null>
    material: InletDescriptor<MaterialGraph>
    outputMaterial: OutletDescriptor<MaterialGraph>
    size: InletDescriptor<[number, number]>
    useAlpha: InletDescriptor<boolean>
}
const transformColorOverlayDescriptor: TransformColorOverlayDescriptorType = {
    material: TD.inlet(TD.MaterialGraph),
    size: TD.inlet(TD.Tuple<[number, number]>()),
    image: TD.inlet(TD.Nullable(TD.ImageGenerator)),
    useAlpha: TD.inlet(TD.Boolean),
    outputMaterial: TD.outlet(TD.MaterialGraph),
}

export class TransformColorOverlay implements NodeClassImpl<TransformColorOverlayDescriptorType, typeof TransformColorOverlay> {
    static descriptor = transformColorOverlayDescriptor
    static uniqueName = "TransformColorOverlay"
    material!: Inlet<MaterialGraph>
    size!: Inlet<[number, number]>
    image!: Inlet<ImageGenerator | null>
    useAlpha!: Inlet<boolean>
    outputMaterial!: Outlet<MaterialGraph>

    run() {
        const material = this.material
        const image = this.image
        const size = this.size
        const useAlpha = this.useAlpha
        if (material === NotReady || image === NotReady || size === NotReady || useAlpha === NotReady) {
            this.outputMaterial.emitIfChanged(NotReady)
            return
        }
        this.outputMaterial.emitIfChanged(image ? transformColorOverlay(material, image.imageNode, size, useAlpha) : material)
    }
}

function colorOverlay(color: InputMaterialConnection<"color">, image: ImageNodeGenerator, size: [number, number], useAlpha: boolean) {
    const uv = materialConnection(
        new UvMap({
            parameters: {
                inputs: {},
                settings: {uvMapIndex: 0},
            },
        }),
        "uv",
    )
    const vector = materialConnection(
        new Mapping({
            parameters: {
                inputs: {vector: uv},
                settings: {vectorType: "TEXTURE", scale: {x: size[0], y: size[1], z: 1}},
            } as const,
        }),
        "vector",
    )
    const imageNodeData = image.generator({uv: vector, extension: "REPEAT", interpolation: "Linear", projection: "FLAT", size})

    const desaturated = materialConnection(new Hsv({parameters: {inputs: {color}, settings: {hue: 0.5, value: 1, saturation: 0, fac: 1}}}), "color")

    const multiplied = materialConnection(
        new MixRgb({
            parameters: {
                inputs: {color1: desaturated, color2: imageNodeData.color},
                settings: {blendType: "MULTIPLY", fac: 1},
            },
        }),
        "color",
    )

    if (useAlpha) {
        return materialConnection(
            new MixRgb({parameters: {inputs: {color1: color, color2: multiplied, fac: imageNodeData.alpha}, settings: {blendType: "MIX"}}}),
            "color",
        )
    } else {
        return multiplied
    }
}

function getColorInputForBrdfNode(
    node: BsdfPrincipled | BsdfDiffuse,
    convertParameterToNode = true,
    defaultValue = {r: 1, g: 1, b: 1},
): InputMaterialConnection<"color"> | null {
    const colorInput = node instanceof BsdfPrincipled ? node.parameters.inputs.baseColor : node.parameters.inputs.color
    const colorParam = (node instanceof BsdfPrincipled ? node.parameters.settings.baseColor : node.parameters.settings.color) ?? defaultValue
    if (!colorInput && convertParameterToNode) {
        if (!colorParam) {
            return null
        }
        return materialConnection(
            new Color({
                parameters: {
                    inputs: {},
                    settings: {
                        color: colorParam,
                    },
                },
            }),
            "color",
        )
    }
    return colorInput ?? null
}

function setColorInputForBrdfNode(node: BsdfPrincipled | BsdfDiffuse, colorInput: InputMaterialConnection<"color">): void {
    if (node instanceof BsdfPrincipled) {
        node.updateInput("baseColor", colorInput)
    } else if (node instanceof BsdfDiffuse) {
        node.updateInput("color", colorInput)
    }
}

export function transformColorOverlay(originalGraph: MaterialGraph, image: ImageNodeGenerator, size: [number, number], useAlpha = true): MaterialGraph {
    const graph = originalGraph.clone({cloneSubNode: () => true})
    for (const node of graph.nodes) {
        if (node instanceof BsdfPrincipled || node instanceof BsdfDiffuse) {
            const color = getColorInputForBrdfNode(node)
            if (color) {
                setColorInputForBrdfNode(node, colorOverlay(color, image, size, useAlpha))
            }
        }
    }
    return graph
}
